// src/app/api/adminAuthApi.ts
import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   TYPES — matching the FSL UserModel / adminAuth routes exactly
───────────────────────────────────────────────────────────────── */

export type AdminRole = "admin" | "staff" | "super_admin" | "tutor";

export interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  role: AdminRole;
  isEmailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

/* ─── Request shapes ─── */

export interface AdminLoginRequest {
  email: string;
  password: string;
}

export interface SuperAdminRegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface AddTeamMemberRequest {
  email: string;
  firstName: string;
  lastName: string;
  /** Only "admin" or "staff" are accepted by the backend */
  role: Extract<AdminRole, "admin" | "staff" | "tutor">;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateRoleRequest {
  role: Extract<AdminRole, "admin" | "staff" | "tutor">;
}

/* ─── Response shapes ─── */

export interface AdminAuthResponse {
  success: boolean;
  message: string;
  user: AdminUser;
  token: string;
}

export interface AdminUserResponse {
  success: boolean;
  message: string;
  data: AdminUser;
}

export interface TeamListResponse {
  success: boolean;
  message: string;
  count: number;
  data: AdminUser[];
}

export interface AddTeamMemberResponse {
  success: boolean;
  message: string;
  user: AdminUser;
  /** Returned so super-admin can share it manually if email delivery fails */
  temporaryPassword: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
  temporaryPassword: string;
  data: null;
}

interface MessageResponse {
  success: boolean;
  message: string;
  data: null;
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE — injected into the shared baseApi
───────────────────────────────────────────────────────────────── */

export const adminAuthApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Super-admin self-registration (first-time setup only) ──
    superAdminRegister: builder.mutation<
      AdminAuthResponse,
      SuperAdminRegisterRequest
    >({
      query: (data) => ({
        url: "/admin/register",
        method: "POST",
        body: data,
      }),
    }),

    // ── Sign in (admin | staff | super_admin) ──
    adminLogin: builder.mutation<AdminAuthResponse, AdminLoginRequest>({
      query: (credentials) => ({
        url: "/admin/login",
        method: "POST",
        body: credentials,
      }),
    }),

    // ── Logout ──
    adminLogout: builder.mutation<MessageResponse, void>({
      query: () => ({ url: "/admin/logout", method: "POST" }),
    }),

    // ── Current user profile ──
    getAdminMe: builder.query<AdminUserResponse, void>({
      query: () => "/admin/me",
      providesTags: [{ type: "User", id: "ADMIN_CURRENT" }],
    }),

    // ── Change own password (admin | staff | super_admin) ──
    adminChangePassword: builder.mutation<
      MessageResponse,
      ChangePasswordRequest
    >({
      query: (data) => ({
        url: "/admin/change-password",
        method: "POST",
        body: data,
      }),
    }),

    // ─────── super_admin only routes ───────

    // ── List all team members ──
    getTeam: builder.query<TeamListResponse, void>({
      query: () => "/admin/team",
      providesTags: [{ type: "User", id: "TEAM_LIST" }],
    }),

    // ── Add admin or staff account ──
    addTeamMember: builder.mutation<
      AddTeamMemberResponse,
      AddTeamMemberRequest
    >({
      query: (data) => ({
        url: "/admin/add-admin",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "User", id: "TEAM_LIST" }],
    }),

    // ── Update a team member's role ──
    updateTeamMemberRole: builder.mutation<
      AdminUserResponse,
      { id: string } & UpdateRoleRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/team/${id}/role`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "User", id: "TEAM_LIST" }],
    }),

    // ── Remove a team member ──
    removeTeamMember: builder.mutation<MessageResponse, string>({
      query: (id) => ({
        url: `/admin/team/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "User", id: "TEAM_LIST" }],
    }),

    // ── Reset a team member's password ──
    resetTeamMemberPassword: builder.mutation<ResetPasswordResponse, string>({
      query: (id) => ({
        url: `/admin/reset-password/${id}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useSuperAdminRegisterMutation,
  useAdminLoginMutation,
  useAdminLogoutMutation,
  useGetAdminMeQuery,
  useAdminChangePasswordMutation,
  useGetTeamQuery,
  useAddTeamMemberMutation,
  useUpdateTeamMemberRoleMutation,
  useRemoveTeamMemberMutation,
  useResetTeamMemberPasswordMutation,
} = adminAuthApi;
